import { useMemo, useState, useEffect } from "react";
import { SEO } from "@/components/seo/SEO";
import { generateBreadcrumbSchema, generateLearningResourceSchema } from "@/lib/seoSchemas";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ModernMCQCard, type MCQ } from "@/components/practice/ModernMCQCard";
import { MockExamSetup, type ExamConfig } from "@/components/mock/MockExamSetup";
import { EnhancedTimer } from "@/components/mock/EnhancedTimer";
import { MCQNavigationPalette } from "@/components/navigation/MCQNavigationPalette";
import { FloatingNavigationBar } from "@/components/navigation/FloatingNavigationBar";
import { UniversalBreadcrumb } from "@/components/navigation/UniversalBreadcrumb";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useQuestionStatus } from "@/hooks/useQuestionStatus";
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation";
import { useNavigationKeyboard } from "@/hooks/useNavigationKeyboard";
import { useExamContext } from "@/contexts/ExamContext";
import { ExamSwitcher } from "@/components/dashboard/ExamSwitcher";
import { toast } from "sonner";
import { SmartNextSteps } from "@/components/navigation/SmartNextSteps";

const sb = supabase as unknown as SupabaseClient<any>;

const Mock = () => {
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [examConfig, setExamConfig] = useState<ExamConfig | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [showPalette, setShowPalette] = useState(false);

  type DbMcqRow = {
    id: string;
    stem: string;
    option_a: string;
    option_b: string;
    option_c: string;
    option_d?: string | null;
    option_e?: string | null;
    correct_option: 'A' | 'B' | 'C' | 'D' | 'E';
    explanation?: string | null;
    difficulty: number;
    tags: string[] | null;
  };

  const { selectedExamSlug } = useExamContext();

  // Get labelId for the selected exam
  const { data: selectedLabelRow } = useQuery({
    queryKey: ["exam-label-for-mock", selectedExamSlug],
    enabled: !!selectedExamSlug,
    queryFn: async () => {
      if (!selectedExamSlug) return null;
      const { data, error } = await sb
        .from("exam_labels")
        .select("id")
        .eq("slug", selectedExamSlug)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
  });

  const labelId = selectedExamSlug ? selectedLabelRow : null;

  // Get topic IDs that have MCQs for the selected exam
  const { data: examTopicIds } = useQuery({
    queryKey: ["exam-topic-ids-mock", labelId, selectedExamSlug],
    enabled: !!selectedExamSlug && !!labelId,
    queryFn: async () => {
      if (!labelId) return null;
      const { data: labelMcqs, error: labelErr } = await sb
        .from("mcq_exam_labels")
        .select("mcq_id")
        .eq("label_id", labelId);
      if (labelErr) throw labelErr;
      const mcqIds = (labelMcqs || []).map((r: any) => r.mcq_id);
      if (mcqIds.length === 0) return new Set<string>();
      const { data: mcqs, error: mcqErr } = await sb
        .from("mcqs")
        .select("topic_id")
        .in("id", mcqIds);
      if (mcqErr) throw mcqErr;
      return new Set((mcqs || []).map((m: any) => m.topic_id).filter(Boolean));
    },
  });

  // Fetch topics for configuration - filtered by exam
  const { data: allTopics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: async () => {
      const { data, error } = await sb.from("topics").select("id,name").order("name", { ascending: true });
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const topics = useMemo(() => {
    if (!selectedExamSlug || !examTopicIds) return allTopics;
    return allTopics.filter(t => examTopicIds.has(t.id));
  }, [allTopics, examTopicIds, selectedExamSlug]);

  // Fetch MCQs based on exam configuration
  const { data: mcqRows = [], isLoading } = useQuery({
    queryKey: ["mock-mcqs", examConfig, labelId, selectedExamSlug],
    queryFn: async () => {
      if (!examConfig) return [];
      
      let query = sb
        .from("mcqs")
        .select("id, question, options, correct_index, explanation, difficulty, tags")
        .limit(examConfig.questionCount * 2); // Fetch more than needed for shuffling

      // Hierarchical topic filtering: include parent + all child topics
      if (examConfig.topicId) {
        const { data: childTopics, error: childError } = await sb
          .from("topics")
          .select("id")
          .eq("parent_topic_id", examConfig.topicId);
        
        if (childError) throw childError;
        
        const topicIds = [examConfig.topicId, ...(childTopics || []).map(t => t.id)];
        query = query.in("topic_id", topicIds);
      }
      
      if (examConfig.difficulty) {
        query = query.eq("difficulty", examConfig.difficulty);
      }

      // Apply label filtering
      if (selectedExamSlug && labelId) {
        // Specific exam selected
        const { data: labelMcqs, error: labelError } = await sb
          .from("mcq_exam_labels")
          .select("mcq_id")
          .eq("label_id", labelId);
        
        if (labelError) throw labelError;
        
        const mcqIds = labelMcqs.map(item => item.mcq_id);
        if (mcqIds.length > 0) {
          query = query.in("id", mcqIds);
        } else {
          return []; // No MCQs for selected exam
        }
      } else if (selectedExamSlug === null) {
        // "All Exams" mode - show unlabeled MCQs (fallback)
        const { data: allLabels, error: allLabelsErr } = await sb.from("mcq_exam_labels").select("mcq_id");
        if (allLabelsErr) throw allLabelsErr;
        const labeledIds = (allLabels || []).map(l => l.mcq_id);
        if (labeledIds.length > 0) {
          const inList = `(${labeledIds.map((id: string) => `"${id}"`).join(",")})`;
          query = query.not("id", "in", inList);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // Return DB format - transformation happens in useMemo below
      return (data || []) as unknown as DbMcqRow[];
    },
    enabled: !!examConfig,
  });

  // Transform and shuffle MCQs
  const mcqs: MCQ[] = useMemo(() => {
    const transformed = (mcqRows || []).map((q) => ({
      id: q.id,
      stem: q.stem,
      options: [
        q.option_a,
        q.option_b,
        q.option_c,
        q.option_d,
        q.option_e
      ].filter(Boolean),
      correctIndex: ['A', 'B', 'C', 'D', 'E'].indexOf(q.correct_option),
      explanation: q.explanation || undefined,
      tags: q.tags || [],
      difficulty: q.difficulty,
    }));
    
    // Shuffle and take the required number
    const shuffled = [...transformed].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, examConfig?.questionCount || 0);
  }, [mcqRows, examConfig]);

  const total = mcqs.length;

  // Question status management using the new hook
  const {
    questionStatuses,
    setQuestionStatus,
    toggleFlag,
    markAnswered,
    getQuestionStatus,
    resetQuestionStatus,
    clearAnswer
  } = useQuestionStatus(total);

  // Initialize question statuses when exam starts
  useEffect(() => {
    if (started && total > 0) {
      resetQuestionStatus();
    }
  }, [started, resetQuestionStatus, total]);

  // Track time spent on current question
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [index]);

  // Update time spent when question changes
  useEffect(() => {
    const updateTimeSpent = () => {
      if (started && !finished) {
        const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
        setQuestionStatus(index, { timeSpent });
      }
    };

    const interval = setInterval(updateTimeSpent, 1000);
    return () => clearInterval(interval);
  }, [index, started, finished, questionStartTime, setQuestionStatus]);

  const handleExamStart = (config: ExamConfig) => {
    setExamConfig(config);
    setStarted(true);
    setFinished(false);
    setIndex(0);
    setScore(0);
    toast.success(`Starting mock exam with ${config.questionCount} questions!`);
  };

  const handleQuestionSelect = (questionIndex: number) => {
    setIndex(questionIndex);
    setQuestionStartTime(Date.now());
  };

  const handlePrevious = () => {
    if (index > 0) {
      setIndex(index - 1);
      setQuestionStartTime(Date.now());
    }
  };

  const handleNext = () => {
    if (index < total - 1) {
      setIndex(index + 1);
      setQuestionStartTime(Date.now());
    }
  };

  const handleFlag = () => {
    toggleFlag(index);
    toast.success(getQuestionStatus(index).flagged ? "Question unflagged" : "Question flagged for review");
  };

  const handleSubmit = (selectedIndex: number, isCorrect: boolean) => {
    if (isCorrect) {
      setScore(s => s + 1);
    }
    
    markAnswered(index, selectedIndex, isCorrect);
  };

  const handleReset = () => {
    setIndex(0);
    setScore(0);
    resetQuestionStatus();
  };

  const handleClearAnswer = () => {
    const wasCorrect = getQuestionStatus(index).correct;
    clearAnswer(index);
    if (wasCorrect) {
      setScore(s => Math.max(0, s - 1));
    }
    toast.success("Answer cleared. You can re-attempt this question.");
  };

  const handleWarning = (minutes: number) => {
    toast.warning(`${minutes} minute${minutes === 1 ? '' : 's'} remaining!`);
  };

  const handleFinish = () => {
    setFinished(true);
    toast.success("Mock exam completed!");
  };

  // Navigation shortcuts and gestures
  useNavigationKeyboard({
    onNext: handleNext,
    onPrevious: handlePrevious,
    onTogglePalette: () => setShowPalette(!showPalette),
    onFlag: handleFlag,
    onReset: handleReset,
    enabled: started && !finished
  });

  useSwipeNavigation({
    onSwipeLeft: handleNext,
    onSwipeRight: handlePrevious,
    enabled: started && !finished
  });

  const progress = total ? ((index + 1) / total) * 100 : 0;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  
  const schemas = [
    generateBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Mock Exams", path: "/mock" }
    ], origin),
    generateLearningResourceSchema(
      "Medical Mock Exams",
      "Timed mock exams simulating real medical licensing exams. Practice under exam conditions with detailed analytics and performance tracking.",
      `${origin}/mock`
    )
  ];

  return (
    <div className="space-y-6">
      <SEO 
        title="Medical Mock Exams - Timed Practice Tests for Medical Students"
        description="Take timed mock exams simulating real medical licensing tests. Comprehensive analytics, question navigation, and detailed performance tracking. Perfect for USMLE, NEET, and medical exam preparation."
        path="/mock"
        keywords="medical mock exams, timed medical tests, medical licensing exam practice, usmle mock test, neet mock exam, medical exam simulator"
        schema={schemas}
      />
      
      {/* Breadcrumb Navigation */}
      <UniversalBreadcrumb />
      
      {/* Exam Switcher - Show before exam starts */}
      {!started && (
        <div className="flex justify-end mb-4">
          <ExamSwitcher />
        </div>
      )}
      
      {!started ? (
        <>
          {mcqRows.length === 0 && !isLoading && (
            <Card className="medical-card mb-6">
              <CardContent className="py-8 text-center">
                <p className="text-yellow-800 dark:text-yellow-200">
                  No MCQs available for the selected exam. Please select a different exam or check your subscription.
                </p>
              </CardContent>
            </Card>
          )}
          <MockExamSetup
            totalQuestions={mcqRows.length}
            topics={topics}
            onStart={handleExamStart}
            isLoading={isLoading}
          />
        </>
      ) : !finished ? (
        <div className="space-y-4">
          {/* Header with Timer and Controls */}
          <div className="flex items-center justify-between gap-4">
            <EnhancedTimer
              totalMinutes={examConfig?.timeLimit || 15}
              onComplete={handleFinish}
              onWarning={handleWarning}
              showProgress={true}
            />
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPalette(!showPalette)}
                className="hidden md:flex"
              >
                {showPalette ? "Hide" : "Show"} Palette
              </Button>
              <Button variant="destructive" size="sm" onClick={handleFinish}>
                End Exam
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex gap-6">
            {/* Question Content */}
            <div className="flex-1 space-y-4">
              {/* Question Palette - Mobile Compact */}
              <div className="md:hidden">
                <MCQNavigationPalette
                  totalQuestions={total}
                  currentQuestion={index}
                  questionStatuses={questionStatuses}
                  onQuestionSelect={handleQuestionSelect}
                  compact
                />
              </div>

              <ModernMCQCard 
                mcq={mcqs[index]} 
                onSubmit={handleSubmit}
                onClearAnswer={handleClearAnswer}
                showTimer={false}
                mode="exam"
              />
            </div>

            {/* Question Palette - Desktop */}
            {showPalette && (
              <div className="hidden md:block">
                <MCQNavigationPalette
                  totalQuestions={total}
                  currentQuestion={index}
                  questionStatuses={questionStatuses}
                  onQuestionSelect={handleQuestionSelect}
                />
              </div>
            )}
          </div>

          {/* Floating Navigation Bar */}
          <FloatingNavigationBar
            currentQuestion={index}
            totalQuestions={total}
            progress={progress}
            canGoPrevious={index > 0}
            canGoNext={index < total - 1}
            isFlagged={getQuestionStatus(index).flagged}
            showPalette={showPalette}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onFlag={handleFlag}
            onTogglePalette={() => setShowPalette(!showPalette)}
            onReset={handleReset}
          />
        </div>
      ) : (
        <Card className="medical-card">
          <CardHeader>
            <CardTitle className="text-2xl">Exam Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Score Overview */}
            <div className="text-center space-y-2">
              <div className="text-6xl font-bold text-medical-primary">
                {total ? Math.round((score / total) * 100) : 0}%
              </div>
              <p className="text-xl text-muted-foreground">
                {score} out of {total} questions correct
              </p>
            </div>

            {/* Performance Summary */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="text-center p-4 bg-medical-green/10 rounded-lg border border-medical-green/20">
                <div className="text-2xl font-bold text-medical-green">
                  {questionStatuses.filter(q => q.correct === true).length}
                </div>
                <p className="text-sm text-muted-foreground">Correct Answers</p>
              </div>
              
              <div className="text-center p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                <div className="text-2xl font-bold text-destructive">
                  {questionStatuses.filter(q => q.answered && q.correct === false).length}
                </div>
                <p className="text-sm text-muted-foreground">Incorrect</p>
              </div>
              
              <div className="text-center p-4 bg-orange-100 rounded-lg border border-orange-300 dark:bg-orange-900/20 dark:border-orange-700">
                <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                  {questionStatuses.filter(q => q.flagged).length}
                </div>
                <p className="text-sm text-muted-foreground">Flagged</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => {
                  setStarted(false);
                  setFinished(false);
                  setIndex(0);
                  setScore(0);
                  setExamConfig(null);
                  resetQuestionStatus();
                }}
                className="bg-gradient-medical"
              >
                Take Another Exam
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setStarted(true);
                  setFinished(false);
                  setIndex(0);
                  setScore(0);
                  resetQuestionStatus();
                }}
              >
                Retake Same Exam
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart Next Steps - Show after exam completion */}
      {finished && (
        <div className="mt-8">
          <SmartNextSteps />
        </div>
      )}
    </div>
  );
};

export default Mock;